import {
  type ForecastData,
  ForecastDataImpl,
  ForecastDetailData,
  models,
  SubForecastData,
} from 'cesg-shared';
import { appHttpService } from '../standard/http-service';

class ForecastService {
  async getAll(projectId: string, type: string): Promise<ForecastData[]> {
    const forecasts = await appHttpService.request
      .url(`/forecasts/${projectId}`)
      .query({ type: type })
      .get()
      .json<models.ForecastResponse[]>();
    return forecasts.map((f) => new ForecastDataImpl(f));
  }

  async updateForecast(
    forecastId: string,
    body: models.UpdateForecastBody,
  ): Promise<ForecastData | unknown> {
    const forecasts = await appHttpService.request
      .url(`/forecasts/forecast/${forecastId}`)
      .put(body)
      .json<models.ForecastResponse>();
    return new ForecastDataImpl(forecasts);
  }

  async updateSubforecast(
    subforecastId: string,
    body: models.UpdateSubForecastBody,
  ): Promise<SubForecastData | unknown> {
    const forecasts = await appHttpService.request
      .url(`/forecasts/subforecast/${subforecastId}`)
      .put(body)
      .json<models.SubForecastResponse>();
    return new SubForecastData(forecasts);
  }

  async updateForecastdetail(
    forecastDetailId: string,
    body: models.UpdateForecastDetailBody,
  ): Promise<ForecastDetailData | unknown> {
    const forecasts = await appHttpService.request
      .url(`/forecasts/forecastdetail/${forecastDetailId}`)
      .put(body)
      .json<models.ForecastDetailResponse>();
    return new ForecastDetailData(forecasts);
  }

  async newForecastDetails(
    subForecastId: string,
    createBefore?: boolean,
  ): Promise<ForecastDetailData> {
    const forecasts = await appHttpService.request
      .url(`/forecasts/forecastdetail/${subForecastId}`)
      .query({ createBefore: createBefore })
      .post()
      .json<models.ForecastDetailResponse>();
    return new ForecastDetailData(forecasts);
  }

  async exportToCsv(projectId: string, type: string): Promise<string> {
    return await appHttpService.request
      .url(`/forecasts/${projectId}/export`)
      .query({ type: type })
      .post()
      .text();
  }

  async deleteForecastDetails(
    forecastdetailId: string,
    createBefore?: boolean,
  ): Promise<void> {
    const request = appHttpService.request.url(
      `/forecasts/forecastdetail/${forecastdetailId}`,
    );

    if (createBefore !== undefined) {
      request.query({ createBefore });
    }

    await request.json({}).delete().res();
  }

  async updateForecastDetailsComment(
    forecastDetailId: string,
    data: models.UpdateForecastDetailCommentBody,
  ): Promise<ForecastDetailData | unknown> {
    const forecasts = await appHttpService.request
      .url(`/forecasts/forecastdetail/${forecastDetailId}/comment`)
      .put({ comment: data.comment })
      .json<models.ForecastDetailResponse>();
    return new ForecastDetailData(forecasts);
  }

  async updateForecastComment(
    forecastId: string,
    data: models.UpdateForecastCommentBody,
  ): Promise<ForecastData> {
    const forecasts = await appHttpService.request
      .url(`/forecasts/forecast/${forecastId}/comment`)
      .put({ comment: data.comment })
      .json<models.ForecastResponse>();
    return new ForecastDataImpl(forecasts);
  }
}

export const forecastService = new ForecastService();
